import type { ComponentProps } from "react";
import { useRef, useState } from "react";
import { LuSendHorizontal } from "react-icons/lu";

import { Spinner } from "@/components/shared/spinner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/utils/lib";

import { useEditorContext } from "../editor-context";
import { getGatedContentValidationError } from "../editor-gated-content-utils";

type EditorActionSubmitProps = ComponentProps<typeof Button> & {
  preserveFocusOnPointerDown?: boolean;
};

export const EditorActionSubmit = ({
  className,
  title = "Post",
  onClick,
  onPointerDown,
  disabled,
  preserveFocusOnPointerDown = false,
  ...props
}: EditorActionSubmitProps) => {
  const editorContext = useEditorContext();
  const {
    empty,
    uploading,
    onSubmit,
    submitting,
    errorMessages,
    setEditorContext,
  } = editorContext;
  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const hasErrors = Object.values(errorMessages).some(
    (messages) => !!messages.size,
  );

  return (
    <Popover open={showErrors && hasErrors}>
      <Button
        {...props}
        ref={anchorRef}
        className={cn("relative", className)}
        disabled={!!disabled || empty() || uploading() || submitting}
        onPointerDown={(event) => {
          onPointerDown?.(event);

          if (
            event.defaultPrevented ||
            !preserveFocusOnPointerDown ||
            (event.pointerType === "mouse" && event.button !== 0)
          ) {
            return;
          }

          event.preventDefault();
        }}
        onClick={(e) => {
          const syncedEditorContext = editorContext.syncLiveTextFields();
          const gatedContentError =
            getGatedContentValidationError(syncedEditorContext);

          if (gatedContentError) {
            setEditorContext({
              errorMessages: {
                ...errorMessages,
                gatedBody: new Set(errorMessages.gatedBody).add(
                  gatedContentError,
                ),
              },
            });
            setShowErrors(true);
            return;
          }

          if (hasErrors) {
            setShowErrors(true);
            return;
          }
          onClick?.(e);
          onSubmit(syncedEditorContext);
        }}
      >
        <span
          className={cn(
            `
              hidden
              md:inline
            `,
            {
              invisible: submitting,
              "hidden!": !title,
            },
          )}
        >
          {title}
        </span>
        <LuSendHorizontal
          className={cn(
            `
              text-lg
              md:hidden
            `,
            {
              invisible: submitting,
              "block!": !title,
            },
          )}
        />
        {submitting && (
          <div className="absolute inset-0 place-content-center">
            <Spinner size={20} className="relative top-[3px]" />
          </div>
        )}
      </Button>
      <PopoverContent anchor={anchorRef}>
        <ul className="list-disc px-4">
          {Object.values(errorMessages).map((messages, index) =>
            Array.from(messages).map((message, msgIndex) => (
              <li key={`${index}-${msgIndex}`} className="text-sm text-red-500">
                {message}
              </li>
            )),
          )}
        </ul>
        <Button
          className="mt-4 w-full"
          variant="outline"
          onClick={() => {
            setShowErrors(false);
          }}
        >
          Ok
        </Button>
      </PopoverContent>
    </Popover>
  );
};
